import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientLandingDecompositionResult,
  selectCrossClientLandingInitialization,
  selectCrossClientInitLoading,
  selectCrossClientEvaluateLoading,
} from '../../../store/crossClient/crossClientLanding'
import { type PredefinedCrossClientQuery } from '../../../models/CrossClient'
import { CrossClientLandingTabs } from './Tabs/CrossClientLandingTabs'
import { CrossClientLandingInitialization } from './CrossClientLandingInitialization'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { CROSS_CLIENT_FUNDS_START_BLOCKED_MESSAGE } from '../utils/crossClientFunds'
import { useCrossClientFundsQuotas } from '../utils/crossClientFundsQuotas'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { useCrossClientQueryLandingExecution, MAX_USER_QUESTION_LENGTH } from './hooks'
import { useCrossClientQuerySettings } from './crossClientSettings'
import { CrossClientQueryInput } from './CrossClientQueryInput'
import { CrossClientChatReview, CrossClientChatReviewActions } from './CrossClientChatReview'
import { CrossClientQueryError } from './CrossClientQueryError'
import { CrossClientClientsPill } from './Pills/CrossClientClientsPill'
import { CrossClientSourcesPill } from './Pills/CrossClientSourcesPill'
import './CrossClientLanding.scss'

export const CrossClientLanding = () => {
  const { tracked, trackingClient } = useTracking()
  const { hasScope } = useZocksAuth()

  const { state: locationState, search: locationSearch } = useLocation()
  const navigate = useNavigate()

  const isAdmin = hasScope('**', 'cross_client_query:admin', [])

  const decompositionResult = useAppSelector(selectCrossClientLandingDecompositionResult)
  const initialization = useAppSelector(selectCrossClientLandingInitialization)
  const initLoading = useAppSelector(selectCrossClientInitLoading)
  const evaluateLoading = useAppSelector(selectCrossClientEvaluateLoading)

  const { canStart, quotaLoading } = useCrossClientFundsQuotas()

  const [userQuestion, setUserQuestion] = useState<string>('')
  const [wasPredefined, setWasPredefined] = useState<boolean>(false)
  const [pendingPredefined, setPendingPredefined] = useState<PredefinedCrossClientQuery | null>(null)
  const { sources, setSources, clientQueryMode, setClientQueryMode } = useCrossClientQuerySettings()

  const { initError, reviewState, decomposeCrossClientQuery, previewPredefinedQuery, finalizeExecution, resetExecution, discardExecution } =
    useCrossClientQueryLandingExecution()

  const atomicClauses = decompositionResult?.decomposition?.atomicClauses ?? []
  const numberOfClauses = atomicClauses.length
  const fundsBlocked = !canStart
  const cannotExecuteNew = fundsBlocked || quotaLoading
  const buttonDisabled = cannotExecuteNew || userQuestion.trim().length < 3 || userQuestion.trim().length > MAX_USER_QUESTION_LENGTH
  const isWorking = initLoading
  const isReviewing = !isWorking && (!!reviewState || numberOfClauses > 0)
  const inputMode = isWorking ? 'working' : isReviewing ? 'review' : 'input'
  const chatCardStateClass = isWorking
    ? 'cross-client-chat-card--working'
    : isReviewing
      ? 'cross-client-chat-card--review'
      : cannotExecuteNew
        ? 'cross-client-chat-card--disabled'
        : userQuestion.trim().length > 0
          ? 'cross-client-chat-card--typed'
          : ''
  const showBackdrop = isWorking || isReviewing

  const triggerPredefinedDecompose = useCallback(
    async (predefinedQuery: PredefinedCrossClientQuery) => {
      setUserQuestion(predefinedQuery.question)
      setWasPredefined(true)
      navigate(`/cross-client${locationSearch}`, { state: { queryInProgress: true } })
      await decomposeCrossClientQuery({
        userQuestion: predefinedQuery.question,
        sources,
        clientQueryMode,
        predefinedQueryId: predefinedQuery.id,
        decomposition: predefinedQuery.decomposition,
        nnfDecomposition: predefinedQuery.nnfDecomposition ?? undefined,
      })
    },
    [decomposeCrossClientQuery, navigate, sources, clientQueryMode, locationSearch],
  )

  const handleViewResults = tracked(
    async () => {
      if (!initialization) {
        return
      }
      await finalizeExecution(initialization.executionId)
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_CONFIRMED,
      eventDetails: {
        type: wasPredefined ? 'pre-defined' : 'custom',
        query: userQuestion,
        sharedMode: clientQueryMode,
      },
    },
  )

  const restart = useCallback(() => {
    resetExecution()
    setUserQuestion('')
    setWasPredefined(false)
    setPendingPredefined(null)
    navigate(`/cross-client${locationSearch}`, { replace: true, state: null })
  }, [resetExecution, navigate, locationSearch])

  const handleRestart = tracked(
    () => {
      void discardExecution()
      // Clear the execution synchronously before navigating so the state-sync effect doesn't see a stale
      // decompositionResult and fire restart() (which would also wipe the question we want to keep).
      resetExecution()
      setWasPredefined(false)
      setPendingPredefined(null)
      navigate(`/cross-client${locationSearch}`, { replace: true, state: null })
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_DENIED,
      eventDetails: { type: wasPredefined ? 'pre-defined' : 'custom', query: userQuestion },
    },
  )

  const handleInitializeCustomCrossClientQuery = tracked(
    async () => {
      setWasPredefined(false)
      setPendingPredefined(null)
      navigate('/cross-client', { state: { queryInProgress: true } })
      await decomposeCrossClientQuery({
        userQuestion,
        sources,
        clientQueryMode,
      })
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'custom',
        query: userQuestion,
        sources: sources.map((source) => (source.type === 'integration' ? source.integrationType : source.type)),
      },
    },
  )

  const handlePredefinedCrossClientQuerySelected = useCallback(
    (predefinedQuery: PredefinedCrossClientQuery) => {
      setUserQuestion(predefinedQuery.question)
      setPendingPredefined(predefinedQuery)
      previewPredefinedQuery(predefinedQuery.decomposition, sources)
    },
    [previewPredefinedQuery, sources],
  )

  const handleStartPredefinedCrossClientQuery = tracked(
    async (predefinedQuery: PredefinedCrossClientQuery) => {
      await triggerPredefinedDecompose(predefinedQuery)
    },
    {
      eventName: TrackEvent.X_CLIENT_QUERY_SUBMITTED,
      eventDetails: {
        type: 'pre-defined',
      },
    },
    {
      eventDetailsExtractor: (predefinedQuery: PredefinedCrossClientQuery) => {
        return { query: predefinedQuery.question }
      },
    },
  )

  const handleSubmit = () => {
    if (pendingPredefined && pendingPredefined.question === userQuestion) {
      return handleStartPredefinedCrossClientQuery(pendingPredefined)
    }
    return handleInitializeCustomCrossClientQuery()
  }

  useEffect(() => {
    trackingClient.track({
      eventName: TrackEvent.X_CLIENT_OPENED,
    })
  }, [trackingClient])

  useEffect(() => {
    if (!locationState?.queryInProgress && decompositionResult) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs local form state to the router: when navigating back to the landing without an in-progress query, discard the stale decomposition.
      restart()
    }
  }, [locationState?.queryInProgress, decompositionResult, restart])

  return (
    <>
      <CrossClientLandingInitialization />
      <div className='truly-full-size-content d-flex flex-column align-items-center position-relative cross-client-landing-container'>
        <div className='d-flex flex-column zocks-gap-2 w-100 cross-client-landing-content'>
          <div className='d-flex flex-column gap-1'>
            <h5 className='mb-0'>{isAdmin ? 'Team queries' : 'Your queries'}</h5>
            {isAdmin ? <div className='paragraph-small zocks-text-gray-700'>All queries across your team</div> : null}
          </div>
          {initError ? <CrossClientQueryError onRestart={handleRestart} /> : null}
          <CrossClientLandingTabs isAdmin={isAdmin} runDisabled={cannotExecuteNew} onPredefinedQuerySelect={handlePredefinedCrossClientQuerySelected} />
        </div>
        {showBackdrop ? <div className='position-absolute top-0 start-0 end-0 bottom-0 z-1 cross-client-landing-backdrop' /> : null}
        <div className='cross-client-landing-dock position-sticky bottom-0 w-100 z-2 d-flex flex-column align-items-center mt-auto gap-2 px-3'>
          <div className={classNames('cross-client-chat-card', chatCardStateClass)}>
            <CrossClientQueryInput
              userQuestion={userQuestion}
              onQuestionChange={setUserQuestion}
              onSubmit={handleSubmit}
              disabled={cannotExecuteNew || initLoading || !!decompositionResult}
              submitDisabled={buttonDisabled}
              isLoading={initLoading}
              hasExecution={!!decompositionResult}
              mode={inputMode}
              reviewContent={isReviewing && reviewState ? <CrossClientChatReview reviewState={reviewState} /> : null}
              footerRight={
                isReviewing && reviewState ? (
                  <CrossClientChatReviewActions
                    reviewState={reviewState}
                    onViewResults={handleViewResults}
                    onRestart={handleRestart}
                    onStart={handleSubmit}
                    onCancel={restart}
                    isLoading={evaluateLoading}
                  />
                ) : null
              }
              footerLeft={
                <>
                  <CrossClientClientsPill
                    mode={clientQueryMode}
                    onChange={setClientQueryMode}
                    disabled={cannotExecuteNew || initLoading || !!decompositionResult || evaluateLoading}
                  />
                  <CrossClientSourcesPill
                    sources={sources}
                    onChange={setSources}
                    disabled={cannotExecuteNew || initLoading || !!decompositionResult || evaluateLoading}
                  />
                </>
              }
            />
          </div>
          {fundsBlocked ? (
            <div className='d-flex gap-2 mt-2 py-1 px-2 zocks-bg-color-danger-background zocks-text-danger-200 zocks-border-0.5 align-items-center align-self-center'>
              <i className='bi bi-ban fs-6' />
              <div className='paragraph-small'>{CROSS_CLIENT_FUNDS_START_BLOCKED_MESSAGE}</div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
